import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  SELECTION_CHANGE_COMMAND,
  FORMAT_TEXT_COMMAND,
} from 'lexical';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { mergeRegister } from '@lexical/utils';
import { createPortal } from 'react-dom';

const LowPriority = 1;

function getSelectedNode(selection) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
  }
}

function $isAtNodeEnd(point) {
  if (point.type === 'text') {
    return point.offset === point.getNode().getTextContentSize();
  }
  return point.offset === point.getNode().getChildrenSize();
}

function positionTooltip(tooltip, rect) {
  if (rect === null) {
    tooltip.style.opacity = '0';
    tooltip.style.top = '-1000px';
    tooltip.style.left = '-1000px';
  } else {
    tooltip.style.opacity = '1';
    tooltip.style.top = `${rect.top + window.pageYOffset - 50}px`;
    tooltip.style.left = `${
      rect.left + window.pageXOffset - tooltip.offsetWidth / 2 + rect.width / 2
    }px`;
  }
}

function TextSelectionTooltip({ editor }) {
  const tooltipRef = useRef(null);
  const linkInputRef = useRef(null);
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [lastSelection, setLastSelection] = useState(null);

  const updateTooltip = useCallback(() => {
    const selection = $getSelection();
    const tooltipElem = tooltipRef.current;
    const nativeSelection = window.getSelection();
    const activeElement = document.activeElement;

    if (tooltipElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();

    if (
      selection !== null &&
      $isRangeSelection(selection) &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const domRange = nativeSelection.getRangeAt(0);
      let rect;
      if (nativeSelection.anchorNode === rootElement) {
        let inner = rootElement;
        while (inner.firstElementChild !== null) {
          inner = inner.firstElementChild;
        }
        rect = inner.getBoundingClientRect();
      } else {
        rect = domRange.getBoundingClientRect();
      }

      positionTooltip(tooltipElem, rect);

      // Update format states
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsCode(selection.hasFormat('code'));

      // Update link state
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
        // Get current link URL if editing existing link
        const linkNode = $isLinkNode(parent) ? parent : node;
        setLinkUrl(linkNode.getURL());
      } else {
        setIsLink(false);
        setLinkUrl('');
      }

      // Store selection for link creation
      setLastSelection(selection);
    } else if (!activeElement || (activeElement.className !== 'tooltip-input' && activeElement.className !== 'link-input-tooltip')) {
      positionTooltip(tooltipElem, null);
      setIsLink(false);
      setShowLinkInput(false);
      setLinkUrl('');
      setLastSelection(null);
    }

    return true;
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateTooltip();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateTooltip();
          return true;
        },
        LowPriority
      )
    );
  }, [editor, updateTooltip]);

  const formatText = (format) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const insertLink = () => {
    if (!isLink) {
      // Show link input for new link creation
      setShowLinkInput(true);
      setLinkUrl('');
      // Focus the input after state update
      setTimeout(() => {
        if (linkInputRef.current) {
          linkInputRef.current.focus();
        }
      }, 0);
    } else {
      // Remove existing link
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      setShowLinkInput(false);
    }
  };

  const handleLinkSubmit = () => {
    if (linkUrl.trim() && lastSelection) {
      // Normalize URL
      let normalizedUrl = linkUrl.trim();
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = `https://${normalizedUrl}`;
      }

      // Apply link to the stored selection
      editor.update(() => {
        if (lastSelection) {
          lastSelection.insertNodes([]);
          editor.dispatchCommand(TOGGLE_LINK_COMMAND, normalizedUrl);
        }
      });
    }

    setShowLinkInput(false);
    setLinkUrl('');
  };

  const handleLinkKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleLinkSubmit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setShowLinkInput(false);
      setLinkUrl('');
    }
  };

  return (
    <div ref={tooltipRef} className="text-selection-tooltip">
      <div className="tooltip-content">
        <button
          onClick={() => formatText('bold')}
          className={`tooltip-button ${isBold ? 'active' : ''}`}
          aria-label="Format Bold"
          title="Bold"
        >
          <span className="material-symbols-outlined">format_bold</span>
        </button>
        
        <button
          onClick={() => formatText('italic')}
          className={`tooltip-button ${isItalic ? 'active' : ''}`}
          aria-label="Format Italic"
          title="Italic"
        >
          <span className="material-symbols-outlined">format_italic</span>
        </button>
        
        <button
          onClick={() => formatText('underline')}
          className={`tooltip-button ${isUnderline ? 'active' : ''}`}
          aria-label="Format Underline"
          title="Underline"
        >
          <span className="material-symbols-outlined">format_underlined</span>
        </button>
        
        <button
          onClick={() => formatText('strikethrough')}
          className={`tooltip-button ${isStrikethrough ? 'active' : ''}`}
          aria-label="Format Strikethrough"
          title="Strikethrough"
        >
          <span className="material-symbols-outlined">strikethrough_s</span>
        </button>
        
        <button
          onClick={() => formatText('code')}
          className={`tooltip-button ${isCode ? 'active' : ''}`}
          aria-label="Format Code"
          title="Code"
        >
          <span className="material-symbols-outlined">code</span>
        </button>
        
        <div className="tooltip-divider"></div>
        
        <button
          onClick={insertLink}
          className={`tooltip-button ${isLink ? 'active' : ''}`}
          aria-label="Insert Link"
          title="Link"
        >
          <span className="material-symbols-outlined">link</span>
        </button>
      </div>

      {showLinkInput && (
        <div className="tooltip-link-input">
          <input
            ref={linkInputRef}
            type="text"
            className="link-input-tooltip"
            placeholder="Paste or type URL..."
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={handleLinkKeyDown}
            onBlur={() => {
              // Small delay to allow click events to fire
              setTimeout(() => setShowLinkInput(false), 150);
            }}
          />
          <button
            className="link-submit-btn"
            onClick={handleLinkSubmit}
            title="Apply Link"
          >
            <span className="material-symbols-outlined">check</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default function TextSelectionTooltipPlugin() {
  const [editor] = useLexicalComposerContext();
  
  return createPortal(<TextSelectionTooltip editor={editor} />, document.body);
}
